import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import { LinkTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import TextInformation from '@repo/ui/text-information'
import Typography from '@repo/ui/typography'

import { Paths } from '../../../lib/constants/path'
import { useNativeCountryName } from '../../../lib/hooks/use-native-country-name'
import {
  AccomodationEnum,
  accomodationEnumMapping
} from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import { familyArrangementEnumMapping } from '../../../lib/types/enums/ol/family-arrangement.enum'
import { CenterEventStudent } from '../../../lib/types/models/center-event/center-event'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { constructCenterPath } from '../../../views/centers/utils'
import { ParentInformation } from '../enrollment-profile/enrollment-profile-global-view'
import LabelWithText from '../ols/label-with-text'
import { FriendBadge } from '../trip-dates/trip-dates.ui'

type Props = {
  studentInfo: CenterEventStudent
}

const CenterEventStudentInformation = ({ studentInfo }: Props) => {
  const nativeCountryName = useNativeCountryName()
  const { navigate } = useNavigator()
  const priorityParent = EnrollmentProperties.getParentsByPriority(studentInfo)

  const parent1 = EnrollmentProperties.getParentInformation(studentInfo, '1')
  const parent2 = EnrollmentProperties.getParentInformation(studentInfo, '2')

  return (
    <div className="grid grid-cols-2 gap-4 ">
      <div className="flex flex-col gap-3">
        <InnerBox title="Informations sur le jeune" titleColor={tailwindColors.black.DEFAULT}>
          <InnerBox className="bg-white-hover grid grid-cols-4 gap-4 ">
            <LabelWithText
              label="Âge"
              data={`${valueRender(EnrollmentProperties.getAgeAtDeparture(studentInfo))} ans`}
            />
            <LabelWithText
              label="Sexe"
              data={valueRender(
                studentInfo.childGender,
                (value) => genderOneLetterMapping[value].label
              )}
            />
            <LabelWithText
              label="Classe"
              data={valueRender(
                studentInfo.childSchoolLevel,
                (value) => schoolLevelEnumMapping[value]
              )}
            />
            <LabelWithText
              label="# de portable du jeune"
              data={valueRender(studentInfo.childPhoneNumber)}
            />
            <LabelWithText label="CSE" data={'n/a'} />
            <LabelWithText
              className="col-span-2"
              label="Informations santé"
              data={valueRender(studentInfo.medicalInformations)}
            />
            <LabelWithText
              label="Autorisation de sortie"
              data={studentInfo.canGoOutAlone ? 'Oui' : 'Non'}
            />
            <LabelWithText
              label="Modalités d’accueil"
              data={valueRender(
                studentInfo.mainProposition?.familyArrangement,
                (value) => familyArrangementEnumMapping[value].label
              )}
            />
            <LabelWithText
              className="col-span-3"
              label="Amis déclarés"
              data={
                <div className="flex flex-wrap gap-2">
                  {studentInfo.declaredFriends?.length
                    ? studentInfo.declaredFriends.map((friend) => (
                        <FriendBadge
                          key={friend.id}
                          type={friend.type}
                          variant="white"
                          firstName={friend.firstName}
                          lastName={friend.lastName}
                        />
                      ))
                    : '-'}
                </div>
              }
            />
            <LabelWithText
              className="col-span-4"
              label="Amis associés"
              data={
                <div className="flex flex-wrap gap-2">
                  {studentInfo.blockedFriends?.length || studentInfo.blockedByFriends?.length ? (
                    <>
                      {studentInfo.blockedFriends?.map((friend) => (
                        <FriendBadge
                          key={friend.id}
                          type={friend.type}
                          firstName={friend.firstName ?? ''}
                          lastName={friend.lastName ?? ''}
                        />
                      ))}
                      {studentInfo.blockedByFriends?.map((friend) => (
                        <FriendBadge
                          key={`blocked-by-${friend.id}`}
                          type={friend.type}
                          firstName={friend.originalEnrollment?.childFirstName ?? ''}
                          lastName={friend.originalEnrollment?.childLastName ?? ''}
                        />
                      ))}
                    </>
                  ) : (
                    '-'
                  )}
                </div>
              }
            />
          </InnerBox>
        </InnerBox>
        <InnerBox title="Détails séjour et voyage" titleColor={tailwindColors.black.DEFAULT}>
          <InnerBox
            title={studentInfo.mainProposition?.journey.reference}
            className="bg-white-hover grid grid-cols-2 gap-4 "
          >
            <LabelWithText
              label="Formule"
              data={getTranslatedName(studentInfo.mainProposition?.journey.date.product.formula)}
            />
            <LabelWithText
              label="Thème/Option"
              data={[
                getTranslatedName(studentInfo.mainProposition?.journey.mainOption),
                studentInfo.mainProposition?.journey.secondaryOptions
                  .map((option) => getTranslatedName(option.option))
                  .join(', ')
              ]
                .filter(Boolean)
                .join(' / ')}
            />
            <LabelWithText
              label="Voyage principal"
              data={TripProperties.getTripLabel(studentInfo.mainProposition?.mainTrip)}
            />
            <LabelWithText
              label="Voyage secondaire"
              data={valueRender(studentInfo.mainProposition?.secondaryTrip, (trip) =>
                TripProperties.getTripLabel(trip)
              )}
            />

            <TextInformation
              title="Adresse du logement"
              component={
                <div className="flex items-center">
                  {studentInfo.enrollmentCenter?.center.accomodation ===
                  AccomodationEnum.HOST_FAMILY ? (
                    !studentInfo.enrollmentCenter?.hostFamily?.familyName ? (
                      '-'
                    ) : (
                      <>
                        <LinkTwoIcon className="w-5 h-5" color={tailwindColors.blue.DEFAULT} />
                        <Typography.Link
                          variant="tag"
                          className="text-blue"
                          onClick={() => {
                            if (!studentInfo.enrollmentCenter?.hostFamily?.id) return
                            navigate(
                              Paths.OL_HOST_FAMILY_INFORMATION.replace(
                                ':id',
                                studentInfo.enrollmentCenter?.hostFamily.id.toString()
                              )
                            )
                          }}
                        >
                          {studentInfo.enrollmentCenter?.hostFamily?.familyName}
                        </Typography.Link>
                      </>
                    )
                  ) : (
                    <AddressLink
                      address1={studentInfo.enrollmentCenter?.center.journeyPlace?.name ?? ''}
                      country={
                        nativeCountryName(
                          studentInfo.enrollmentCenter?.center.journeyPlace?.country.name
                        ) ?? ''
                      }
                    />
                  )}
                </div>
              }
            />

            <LabelWithText
              label="Type d'hébergement"
              data={valueRender(
                studentInfo.enrollmentCenter?.center.accomodation,
                (value) => accomodationEnumMapping[value]
              )}
            />

            <Typography.Link
              variant="label-medium"
              onClick={() => {
                if (studentInfo.enrollmentCenter?.center?.id)
                  navigate(
                    constructCenterPath(
                      Paths.CENTER_STAFFS_PAGE,
                      studentInfo.enrollmentCenter.center.id
                    )
                  )
              }}
            >
              Voir les encadrants
            </Typography.Link>
          </InnerBox>
        </InnerBox>
      </div>
      <InnerBox
        title="Informations sur les parents"
        titleColor={tailwindColors.black.DEFAULT}
        className="flex flex-col gap-3"
      >
        <div className="flex flex-col gap-2">
          <Badge
            variant="warning"
            text={`Parent 1 ${priorityParent.parent1 === '1' ? ' - Contact prioritaire' : ''}`}
          />
          <ParentInformation parent={parent1} />
        </div>

        <div className="flex flex-col gap-2">
          <Badge
            variant="warning"
            text={`Parent 2 ${priorityParent.parent1 === '2' ? ' - Contact prioritaire' : ''}`}
          />
          <ParentInformation parent={parent2} />
        </div>
        <LabelWithText
          label="Adresse postale"
          data={`${studentInfo.address1}, ${studentInfo.postalCode ?? ''} ${studentInfo.country}`}
        />
      </InnerBox>
    </div>
  )
}

export default CenterEventStudentInformation
